import { useUser } from "@clerk/react";
import { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './SetUpMfa.css';
import { logOut } from "../ApiCenter/API_GET";

const SetUpMfa = ({onComplete}) => {

    const { user, isLoaded } = useUser();

    const [mainMessage, setMainMessage] = useState('Loading MFA set up...');
    const [logOutButton, setLogOutButton] = useState(false);
    const [step, setStep] = useState('initial');
    const [totp, setTotp] = useState(null);
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showKey, setShowKey] = useState(false);

    const inputRefs = useRef([]);

    const submitCode = async (codeString) => {
        setLoading(true);
        setError('');

        try {
            await user.verifyTOTP({ code: codeString });

            if (onComplete) {
                onComplete(setLoading);
            } else {
                window.location.href = '/projects';
                setLoading(false);
            }
        } catch (err) {
            setError(err.errors?.[0]?.longMessage || 'Wrong code. try again.');
            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
            setLoading(false);
        }
    };

    const handleChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.substring(value.length - 1);
        setOtp(newOtp);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        const currentCode = newOtp.join('');
        if (currentCode.length === 6) {
            submitCode(currentCode);
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();

        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split('');
            setOtp(digits);
            inputRefs.current[5]?.focus();
            submitCode(pastedData);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const fullCode = otp.join('');
        if (fullCode.length === 6) {
            submitCode(fullCode);
        }
    };

    const copyKey = () => {
        navigator.clipboard.writeText(totp.secret);
    }

    const isCreatingTotp = useRef(false);
    useEffect(() => {
        if(step !== 'initial' || isCreatingTotp.current) return;

        const handleStartSetup = async () => {
            setLoading(true);
            setError('');
            try {
                isCreatingTotp.current = true;
                setStep('scan');
                setMainMessage(null);
                setLogOutButton(false);
                const res = await user.createTOTP();
                setTotp(res);
            } catch (err) {
                setMainMessage(err.errors?.[0]?.longMessage || 'Error starting MFA setup.');
                setLogOutButton(true);
            } finally {
                isCreatingTotp.current = false;
                setLoading(false);
            }
        };

        handleStartSetup();
    }, [step, user])

    if (!isLoaded) return <div>Cargando...</div>;

    return (
        <div className='mfa-container'>
            <span className='mfa-title'>Two-step verification setup</span>
            <div className='mfa-description'>
                <h1 className=''>Add authenticator application</h1>
                <p>{
                    mainMessage || 'Set up a new sign-in method in your authenticator app and scan the following QR code to link it to your account.'
                }</p>
            </div>
            {logOutButton &&
                <button 
                    className='mfa-submit-button'
                    type="button" 
                    onClick={() => logOut()}
                >
                    Try again
                </button>
            }
            {step === 'scan' && totp && (
                <div className='mfa-body'>
                    <div className='mfa-qr'>
                        <QRCodeSVG value={totp.uri} size={200} />
                        <button onClick={() => setShowKey(p => !p)}>
                            Can't scan QR code? Enter key instead
                        </button>
                        {showKey &&
                            <div className='mfa-qr-key'>
                                <input readOnly type="text" value={totp.secret}/>
                                <button className='bi bi-clipboard-fill' title='Copy key' onClick={copyKey}></button>
                            </div>
                        }
                    </div>
                    <form className='mfa-form' onSubmit={handleSubmit}>
                        <p >Enter the code generated by the application</p>
                        <div 
                            className='mfa-code'
                            onPaste={handlePaste}
                        >
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    autoFocus={index === 0}
                                    maxLength={1}
                                    value={digit}
                                    disabled={loading}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    style={{ background: loading ? '#f5f5f5' : '#fff' }}
                                />
                            ))}
                        </div>
                        {error && <span className="bi bi-exclamation-circle">{error}</span>}
                        <button 
                            className='mfa-submit-button'
                            type="submit" 
                            disabled={loading || otp.join('').length !== 6}
                        >
                            {loading ? 'Verifing...' : 'Continue'}
                        </button>
                        <button 
                            className='mfa-submit-button cancel'
                            type="button" 
                            onClick={() => logOut()}
                        >
                            Back
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
}

export default SetUpMfa;